"use client";
// "use client" hoja interactiva: etapa activa por scroll en PC (>=1024px) y carrusel táctil/auto-avance sin scroll-jack en móvil/tablet (<1024px) — necesita DOM.

// 2026-09-08 ProductShowcase v4 — "Todo en un solo lugar" como Lovable:
// sección oscura full-bleed SIN tarjeta (el recuadro negro gigante fuera),
// título + subtítulo en la columna izquierda, nav de filas con divisores
// hairline y línea gradiente que se LLENA con el scroll de su tramo, y panel
// derecho contenido (no gigante) donde cada etapa entra como DIAPOSITIVA.
// 2026-10-31 v5:
// - En PC (lg >= 1024px): runway alto (100dvh + N×90dvh) + interior sticky
//   h-100dvh; progreso = -rect.top / scrollable → índice activo.
// - En pantallas no-PC (< 1024px, móvil/tablet): el scroll vertical NUNCA
//   secuestra ni dispara cambios rápidos de etapa (antes `total = offsetHeight - innerHeight`
//   valía ~100px y hacía saltar las 5 etapas en 0.1s al scrollear). En <1024px
//   el cambio es por toque/swipe/auto-avance suave en viewport sin mover el scroll.
// - Cero clickbait: etapas reales vía i18n (14 locales), formatos y cuotas reales.

import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type TouchEvent,
} from "react";
import {
  BookOpen,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  GraduationCap,
  Layers,
  Plus,
  Search,
  ArrowUp,
  ArrowUpRight,
} from "lucide-react";
import { useT } from "@/lib/use-i18n";
import type { TranslationKey } from "@/lib/i18n/locales";

type StageId = "place" | "research" | "documents" | "study" | "limits";

const STAGE_ICONS: Record<StageId, typeof Layers> = {
  place: Layers,
  research: Search,
  documents: FileText,
  study: GraduationCap,
  limits: Clock,
};

type Stage = {
  id: StageId;
  titleKey: TranslationKey;
  descKey: TranslationKey;
  /** acento del panel (glow radial + anillo del orbe + inicio del fill) */
  accent: string;
};

// 2026-09-07 honesty: Vortixy truth, no jargon (pipeline/enterprise-grade),
// no competitor copy. Keys keep all 14 locales native.
// 2026-10-31: Dynamic interactive stage animations replace static /showcase/*.webp
// images (which belong exclusively to Admin Announce templates).
const STAGES: Stage[] = [
  {
    id: "place",
    titleKey: "showcaseStage1Title",
    descKey: "showcaseStage1Desc",
    accent: "var(--hero-blue)",
  },
  {
    id: "research",
    titleKey: "showcaseStage2Title",
    descKey: "showcaseStage2Desc",
    accent: "var(--hero-blue)",
  },
  {
    id: "documents",
    titleKey: "showcaseStage3Title",
    descKey: "showcaseStage3Desc",
    accent: "var(--hero-magenta)",
  },
  {
    id: "study",
    titleKey: "showcaseStage4Title",
    descKey: "showcaseStage4Desc",
    accent: "var(--hero-blue)",
  },
  {
    id: "limits",
    titleKey: "showcaseStage5Title",
    descKey: "showcaseStage5Desc",
    accent: "var(--hero-orange)",
  },
];

function ShowcaseStageAnimation({
  stageId,
  t,
}: {
  stageId: StageId;
  t: (key: TranslationKey) => string;
}) {
  const [pulseStep, setPulseStep] = useState(0);

  useEffect(() => {
    if (typeof document !== "undefined" && document.hidden) return;
    const id = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      setPulseStep((prev) => (prev + 1) % 4);
    }, 1850);
    return () => window.clearInterval(id);
  }, [stageId]);

  if (stageId === "place") {
    return (
      <div className="flex h-full min-h-[460px] sm:min-h-[480px] lg:min-h-[500px] w-full flex-col justify-between bg-[var(--card)]/90 p-5 sm:p-6 lg:p-7 select-none">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex size-6.5 items-center justify-center rounded-lg border border-[var(--border-strong)] bg-[var(--background)] text-xs font-bold text-foreground shadow-xs">
              V
            </span>
            <span className="text-xs sm:text-sm font-semibold tracking-tight text-foreground">
              {t("chatTitle")}
            </span>
            <span className="hidden rounded-md border border-[var(--border)] bg-[var(--background)]/80 px-2 py-0.5 font-mono text-[11px] text-muted-foreground sm:inline-block">
              Proyecto: Tesis Cuántica · 3 archivos
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--background)] px-2.5 py-0.5 text-xs font-medium text-foreground">
              <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Espacio activo
            </span>
          </div>
        </div>

        {/* Real workspace conversation */}
        <div className="my-auto space-y-3.5 py-2">
          <div className="ml-auto max-w-[90%] sm:max-w-[82%] rounded-2xl rounded-br-xs border border-[var(--border-strong)] bg-[var(--background)] px-4 py-2.5 text-xs sm:text-[13px] text-foreground shadow-xs">
            Prepara el marco teórico de la investigación con citas primarias y
            genera el documento Word listo para entregar.
          </div>

          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex size-6.5 shrink-0 items-center justify-center rounded-lg border border-[var(--border-strong)] bg-[var(--background)] text-xs font-bold text-foreground shadow-xs">
              V
            </span>
            <div className="min-w-0 flex-1 space-y-3 rounded-2xl rounded-tl-xs border border-[var(--border)] bg-[var(--background)]/60 p-4 shadow-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs font-semibold text-foreground">
                  Marco teórico generado · APA 7
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--card)] px-2.5 py-0.5 text-[11px] font-medium text-foreground">
                  <span className="size-1.5 rounded-full bg-emerald-500" />
                  Listo para entrega
                </span>
              </div>

              {/* Synthesized summary */}
              <p className="text-xs sm:text-[13px] leading-relaxed text-muted-foreground">
                Se estructuró el marco teórico sobre neuroplasticidad y
                retención espaciada integrando 3 publicaciones primarias con DOI
                indexado [1, 2, 3].
              </p>

              {/* Real document card preview */}
              <div className="rounded-xl border border-border/50 bg-[var(--card)]/70 p-3 flex items-center justify-between gap-3 shadow-2xs">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-500/10 text-blue-500">
                    <FileText className="size-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <span className="block truncate text-xs font-semibold text-foreground">
                      Marco_Teorico_Aprendizaje_Activo.docx
                    </span>
                    <span className="block truncate text-[11px] text-muted-foreground">
                      Word editable · 1,850 palabras · 3 fuentes APA 7
                    </span>
                  </div>
                </div>
                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-foreground bg-foreground/[0.05] hover:bg-foreground/[0.08] px-2.5 py-1 rounded-md transition-colors">
                  Abrir <ArrowUpRight className="size-3" />
                </span>
              </div>

              <div className="flex items-center justify-between border-t border-[var(--border)] pt-2 text-xs text-muted-foreground">
                <span>
                  Investigación, redacción y Modo Examen sincronizados
                </span>
                <span className="font-mono text-[11px] font-medium text-foreground">
                  Sin cambiar de pestaña
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Composer bar */}
        <div className="flex items-center justify-between rounded-xl border border-[var(--border-strong)] bg-[var(--background)] px-3.5 py-2 shadow-xs">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Plus className="size-3.5" aria-hidden="true" />
            <span className="truncate">
              Continúa trabajando o pide exportar el documento...
            </span>
          </div>
          <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-lg bg-[var(--foreground)] text-[var(--background)] shadow-xs">
            <ArrowUp className="size-3.5" aria-hidden="true" />
          </span>
        </div>
      </div>
    );
  }

  if (stageId === "research") {
    const sources = [
      { id: "[1]", domain: "Nature Physics", badge: "DOI: 10.1038/s41567" },
      { id: "[2]", domain: "IEEE Quantum", badge: "IEEE Xplore · 2026" },
      { id: "[3]", domain: "Science Advances", badge: "Peer-reviewed · OA" },
    ];
    const activeIdx = pulseStep % sources.length;
    return (
      <div className="flex h-full min-h-[460px] sm:min-h-[480px] lg:min-h-[500px] w-full flex-col justify-between bg-[var(--card)]/90 p-5 sm:p-6 lg:p-7 select-none">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
          <div className="flex items-center gap-2">
            <Search
              className="size-4 text-[var(--hero-blue)]"
              aria-hidden="true"
            />
            <span className="text-xs sm:text-sm font-semibold text-foreground">
              {t("showcaseStage2Title")}
            </span>
            <span className="hidden font-mono text-[11px] text-muted-foreground sm:inline">
              · Deep Research
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--background)] px-2.5 py-0.5 text-xs font-medium text-foreground">
            <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />
            3 fuentes académicas validadas
          </span>
        </div>

        {/* Real research conversation */}
        <div className="my-auto space-y-3.5 py-2">
          {/* User message */}
          <div className="ml-auto max-w-[90%] sm:max-w-[82%] rounded-2xl rounded-br-xs border border-[var(--border-strong)] bg-[var(--background)] px-4 py-2.5 text-xs sm:text-[13px] text-foreground shadow-xs">
            Investiga avances en computación cuántica tolerante a fallos y
            resume con citas directas.
          </div>

          {/* Assistant synthesized report */}
          <div className="flex items-start gap-3">
            <span className="mt-0.5 inline-flex size-6.5 shrink-0 items-center justify-center rounded-lg border border-[var(--border-strong)] bg-[var(--background)] text-xs font-bold text-foreground shadow-xs">
              V
            </span>
            <div className="min-w-0 flex-1 space-y-3 rounded-2xl rounded-tl-xs border border-[var(--border)] bg-[var(--background)]/60 p-4 shadow-xs">
              {/* Horizontal verified sources shelf */}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mr-1">
                  Fuentes:
                </span>
                {sources.map((src, idx) => (
                  <span
                    key={src.id}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 font-mono text-[11px] transition-all duration-300 ${
                      activeIdx === idx
                        ? "border-[var(--border-strong)] bg-[var(--foreground)] text-[var(--background)] shadow-xs"
                        : "border-[var(--border)] bg-[var(--card)] text-muted-foreground"
                    }`}
                  >
                    <span className="font-semibold">{src.id}</span>
                    <span>{src.domain}</span>
                  </span>
                ))}
              </div>

              {/* Synthesized report content */}
              <div className="space-y-1.5">
                <h4 className="text-xs sm:text-sm font-semibold tracking-tight text-foreground">
                  Supresión exponencial de ruido y viabilidad práctica
                </h4>
                <p className="text-xs sm:text-[13px] leading-relaxed text-muted-foreground">
                  Los nuevos experimentos en matrices de transmones demuestran
                  una reducción cuadrática del ruido cuántico{" "}
                  <span className="inline-flex items-center rounded bg-foreground/10 px-1 font-mono text-[10px] font-bold text-foreground">
                    [1]
                  </span>
                  , alcanzando fidelidades de compuerta superiores al 99.9%{" "}
                  <span className="inline-flex items-center rounded bg-foreground/10 px-1 font-mono text-[10px] font-bold text-foreground">
                    [2]
                  </span>
                  . Esto sitúa la corrección de errores dentro del umbral físico
                  requerido para algoritmos en bioinformática y criptografía{" "}
                  <span className="inline-flex items-center rounded bg-foreground/10 px-1 font-mono text-[10px] font-bold text-foreground">
                    [3]
                  </span>
                  .
                </p>
              </div>

              {/* Action bar */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--border)] pt-2.5 text-xs">
                <div className="flex items-center gap-1.5">
                  <span className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--card)] px-2 py-0.5 text-[11px] font-medium text-foreground">
                    <FileText className="size-3 text-muted-foreground" />
                    Word (.docx)
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--card)] px-2 py-0.5 text-[11px] font-medium text-foreground">
                    <FileText className="size-3 text-muted-foreground" />
                    PDF con citas
                  </span>
                </div>
                <span className="font-mono text-[11px] text-emerald-500 flex items-center gap-1">
                  <Check className="size-3" /> Fuentes verificadas
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Composer bar */}
        <div className="flex items-center justify-between rounded-xl border border-[var(--border-strong)] bg-[var(--background)] px-3.5 py-2 shadow-xs">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Plus className="size-3.5" aria-hidden="true" />
            <span className="truncate">
              Pregunta de seguimiento sobre las citas [1, 2, 3]...
            </span>
          </div>
          <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-lg bg-[var(--foreground)] text-[var(--background)] shadow-xs">
            <ArrowUp className="size-3.5" aria-hidden="true" />
          </span>
        </div>
      </div>
    );
  }

  if (stageId === "documents") {
    return (
      <div className="flex h-full min-h-[460px] sm:min-h-[480px] lg:min-h-[500px] w-full flex-col justify-between bg-[var(--card)]/90 p-5 sm:p-6 lg:p-7 select-none">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
          <div className="flex items-center gap-2">
            <FileText
              className="size-4 text-[var(--hero-blue)]"
              aria-hidden="true"
            />
            <span className="text-xs sm:text-sm font-semibold text-foreground">
              Canvas Studio · Documentos listos
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-md border border-[var(--border-strong)] bg-[var(--foreground)] text-[var(--background)] px-2.5 py-1 text-xs font-semibold shadow-xs">
              Descargar .docx
            </span>
            <span className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] bg-[var(--background)] px-2.5 py-1 text-xs font-medium text-foreground">
              Descargar .pdf
            </span>
          </div>
        </div>

        {/* Real document sheet view */}
        <div className="my-auto rounded-xl border border-[var(--border)] bg-[var(--background)]/80 p-5 sm:p-6 shadow-xs space-y-3.5">
          <div className="border-b border-[var(--border)]/70 pb-3">
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              Informe Técnico Oficial · Formato APA 7 / NTC 1486
            </span>
            <h3 className="mt-1 text-sm sm:text-base font-semibold tracking-tight text-foreground">
              Evaluación Experimental de Códigos de Superficie en Arquitecturas
              Cuánticas
            </h3>
          </div>

          <div className="space-y-2 text-xs sm:text-[13px] leading-relaxed text-muted-foreground">
            <p>
              <strong className="text-foreground">1. Resumen Ejecutivo:</strong>{" "}
              La implementación de protocolos de detección de síndromes en
              tiempo real redujo las tasas de error lógico por debajo del umbral
              de tolerancia física [1]. La fidelidad promedio de compuertas
              entrelazantes alcanzó el 99.94% con coherencia T1 optimizada.
            </p>
            <p>
              <strong className="text-foreground">
                2. Referencias Enlazadas:
              </strong>{" "}
              Citas primarias [1, 2, 3] formateadas con DOI activo y listas para
              entrega académica o ejecutiva sin pérdida de maquetación ni
              fuentes rotas.
            </p>
          </div>

          <div className="flex items-center justify-between border-t border-[var(--border)]/70 pt-2.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5 text-emerald-500 font-medium">
              <Check className="size-3.5" /> 100% generado y estructurado
            </span>
            <span className="font-mono text-[11px]">
              Listo para abrir en Word y Google Docs
            </span>
          </div>
        </div>

        {/* Bottom indicator */}
        <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-[var(--border)] pt-2.5">
          <span>Formatos oficiales: Word editable (.docx) · PDF vectorial</span>
          <span className="font-mono text-foreground font-medium">
            Sin suscripciones separadas
          </span>
        </div>
      </div>
    );
  }

  if (stageId === "study") {
    const intervals = [
      "Difícil · 1d",
      "Regular · 3d",
      "Bien · 7d",
      "Fácil · 21d",
    ];
    return (
      <div className="flex h-full min-h-[460px] sm:min-h-[480px] lg:min-h-[500px] w-full flex-col justify-between bg-[var(--card)]/90 p-5 sm:p-6 lg:p-7 select-none">
        {/* Top bar */}
        <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
          <div className="flex items-center gap-2">
            <GraduationCap
              className="size-4 text-[var(--hero-blue)]"
              aria-hidden="true"
            />
            <span className="text-xs sm:text-sm font-semibold text-foreground">
              Modo Examen · Repaso Activo FSRS
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--background)] px-2.5 py-0.5 text-xs font-medium text-foreground">
            <BookOpen className="size-3 text-muted-foreground" />
            Tarjeta 03 / 15
          </span>
        </div>

        {/* Real Exam Mode Card */}
        <div className="my-auto rounded-xl border border-[var(--border)] bg-[var(--background)]/80 p-5 sm:p-6 shadow-xs space-y-3.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="font-mono uppercase tracking-wider text-[11px] text-foreground font-semibold">
              Pregunta de Repaso Activo
            </span>
            <span className="font-mono text-[11px]">Algoritmo FSRS 2026</span>
          </div>

          <p className="text-xs sm:text-[14px] font-semibold text-foreground leading-snug">
            ¿Cuál es el umbral de error por compuerta físico requerido para la
            tolerancia a fallos en códigos de superficie?
          </p>

          <div className="space-y-1.5">
            <div className="rounded-lg border border-[var(--border-strong)] bg-[var(--card)] px-3 py-2 text-xs font-medium text-foreground flex items-center justify-between shadow-2xs">
              <span>A. &lt; 1.0% (Umbral teórico de superficie estándar)</span>
              <span className="font-mono font-semibold text-emerald-500 flex items-center gap-1">
                <Check className="size-3" /> Correcta
              </span>
            </div>
            <div className="rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-xs text-muted-foreground">
              <span>B. &lt; 5.0%</span>
            </div>
            <div className="rounded-lg border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-xs text-muted-foreground">
              <span>C. &lt; 0.05%</span>
            </div>
          </div>

          {/* FSRS rating intervals */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1 border-t border-[var(--border)]/70">
            {intervals.map((iv, idx) => (
              <span
                key={iv}
                className={`rounded-lg border px-2 py-1.5 text-center text-xs font-mono font-medium transition-all ${
                  idx === 2
                    ? "border-[var(--border-strong)] bg-[var(--foreground)] text-[var(--background)] shadow-xs"
                    : "border-[var(--border)] bg-[var(--card)] text-muted-foreground"
                }`}
              >
                {iv}
              </span>
            ))}
          </div>
        </div>

        {/* Bottom bar */}
        <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-[var(--border)] pt-2.5">
          <span>Generado desde tus apuntes, PDFs o videos de YouTube</span>
          <span className="font-mono text-foreground font-medium">
            Exportar a Anki (TSV)
          </span>
        </div>
      </div>
    );
  }

  // Stage 5: limits
  const plans = [
    { name: t("planFreeName"), req: 15, tokens: "200k", pct: 15 },
    { name: t("planGoName"), req: 60, tokens: "400k", pct: 30 },
    { name: t("planProName"), req: 100, tokens: "600k", pct: 50 },
    { name: t("planMaxName"), req: 200, tokens: "1M", pct: 75, active: true },
    { name: t("planMaxplusName"), req: 400, tokens: "2M", pct: 100 },
  ];

  return (
    <div className="flex h-full min-h-[460px] sm:min-h-[480px] lg:min-h-[500px] w-full flex-col justify-between bg-[var(--card)]/90 p-5 sm:p-6 lg:p-7 select-none">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-[var(--border)] pb-3">
        <div className="flex items-center gap-2">
          <Clock
            className="size-4 text-[var(--hero-blue)]"
            aria-hidden="true"
          />
          <span className="text-xs sm:text-sm font-semibold text-foreground">
            Límites claros y transparentes
          </span>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border)] bg-[var(--background)] px-2.5 py-0.5 text-xs font-medium text-foreground">
          <span className="size-1.5 rounded-full bg-emerald-500" />
          Reinicio a las 00:00 UTC
        </span>
      </div>

      {/* Plans quotas list */}
      <div className="my-auto space-y-2 py-2">
        {plans.map((p) => (
          <div
            key={p.name}
            className={`rounded-xl border px-3.5 py-2.5 transition-all ${
              p.active
                ? "border-[var(--border-strong)] bg-[var(--background)] shadow-xs"
                : "border-[var(--border)]/60 bg-[var(--background)]/30"
            }`}
          >
            <div className="flex items-center justify-between text-xs sm:text-[13px] mb-1.5">
              <span className="font-semibold text-foreground flex items-center gap-1.5">
                {p.name}
                {p.active && (
                  <span className="rounded bg-foreground/10 px-1.5 py-0.2 font-mono text-[10px] text-foreground font-normal">
                    Plan actual
                  </span>
                )}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">
                {p.req} respuestas/d · {p.tokens} tokens
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-[var(--card)]">
              <div
                className="h-full rounded-full bg-[var(--foreground)] transition-all duration-700"
                style={{ width: `${p.pct}%` }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Bottom bar */}
      <div className="flex items-center justify-between text-xs text-muted-foreground border-t border-[var(--border)] pt-2.5">
        <span>
          Cuotas justas y claras sin cargos sorpresa ni renovaciones ocultas
        </span>
        <span className="font-mono text-foreground font-medium">
          Reset 00:00 UTC
        </span>
      </div>
    </div>
  );
}

/** Scroll dedicado a cada panel (en dvh) antes de liberar el scroll en PC. */
const PANEL_SCROLL_DVH = 90;

export function ProductShowcase() {
  const t = useT();
  const [activeStage, setActiveStage] = useState(0);
  // Dirección del recorrido: la diapositiva entra desde abajo al bajar
  // y desde arriba al subir (como pasar slides).
  const [dir, setDir] = useState<1 | -1>(1);
  const [userInteractedMobile, setUserInteractedMobile] = useState(false);
  const [inView, setInView] = useState(false);
  const prevRef = useRef(0);
  const dirRef = useRef<1 | -1>(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);

  const applyStageInPlace = (idx: number) => {
    const bounded = ((idx % STAGES.length) + STAGES.length) % STAGES.length;
    const d: 1 | -1 = bounded >= prevRef.current ? 1 : -1;
    prevRef.current = bounded;
    if (d !== dirRef.current) {
      dirRef.current = d;
      setDir(d);
    }
    setActiveStage(bounded);
    const el = containerRef.current;
    if (el) {
      for (let i = 0; i < STAGES.length; i++) {
        el.style.setProperty(`--fill-${i}`, i <= bounded ? "1" : "0");
      }
      el.style.setProperty(
        "--showcase-progress",
        (STAGES.length <= 1 ? 1 : bounded / (STAGES.length - 1)).toFixed(3),
      );
    }
  };

  // Scroll-capture exclusivo para PC (cuando el contenedor tiene runway alto
  // `lg:h-[var(--runway-h)]`, por lo que `total >= window.innerHeight`).
  // En dispositivos no-PC (< 1024px) `containerRef` es `h-auto`: antes `total`
  // valía ~80–150px y un leve scroll de 100px hacía saltar las 5 etapas a toda
  // velocidad. Con `total < window.innerHeight` el scroll vertical en móvil/tablet
  // queda 100% libre y natural.
  useEffect(() => {
    let ticking = false;
    const update = () => {
      ticking = false;
      if (typeof document !== "undefined" && document.hidden) return;
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const total = el.offsetHeight - window.innerHeight;
      // Solo activar scroll-scrubbing si existe el runway sticky de PC (>= 1 viewport extra).
      if (total < window.innerHeight || total <= 0) return;
      const progress = Math.max(0, Math.min(1, -rect.top / total));
      for (let i = 0; i < STAGES.length; i++) {
        const fill = Math.max(0, Math.min(1, progress * STAGES.length - i));
        el.style.setProperty(`--fill-${i}`, fill.toFixed(3));
      }
      el.style.setProperty("--showcase-progress", progress.toFixed(3));
      const idx = Math.min(
        STAGES.length - 1,
        Math.floor(progress * STAGES.length),
      );
      if (idx === prevRef.current) return;
      const d = idx >= prevRef.current ? 1 : -1;
      prevRef.current = idx;
      if (d !== dirRef.current) {
        dirRef.current = d;
        setDir(d);
      }
      setActiveStage(idx);
    };
    const handleScroll = () => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(update);
      }
    };
    const handleVisibility = () => {
      if (typeof document === "undefined" || document.hidden) return;
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      update();
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("online", handleVisibility);
    update();
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("online", handleVisibility);
    };
  }, []);

  // En pantallas < 1024px (sin runway de scroll), detectamos visibilidad y
  // avanzamos suavemente cada 5.5s hasta que la persona toque o deslice.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) setInView(entry.isIntersecting);
      },
      { threshold: 0.25 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!inView || userInteractedMobile) return;
    if (typeof window === "undefined") return;
    if (
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
      false
    ) {
      return;
    }
    const id = window.setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      const el = containerRef.current;
      if (!el) return;
      const total = el.offsetHeight - window.innerHeight;
      // Si estamos en PC con runway sticky activo, el scroll manda.
      if (total >= window.innerHeight && total > 0) return;
      applyStageInPlace((prevRef.current + 1) % STAGES.length);
    }, 5500);
    return () => window.clearInterval(id);
  }, [inView, userInteractedMobile]);

  // Click en el nav → en PC salta al tramo del runway; en < 1024px cambia in-place sin mover el scroll.
  const jumpTo = (idx: number) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const top = window.scrollY + rect.top;
    const total = el.offsetHeight - window.innerHeight;
    if (total < window.innerHeight || total <= 0) {
      setUserInteractedMobile(true);
      applyStageInPlace(idx);
      return;
    }
    const per = total / STAGES.length;
    const reduce =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
    window.scrollTo({
      top: top + (idx + 0.5) * per,
      behavior: reduce ? "auto" : "smooth",
    });
  };

  const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.touches[0];
    if (!touch) return;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    const touch = e.changedTouches[0];
    const startX = touchStartXRef.current;
    const startY = touchStartYRef.current;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    if (!touch || startX === null || startY === null) return;
    const dx = touch.clientX - startX;
    const dy = touch.clientY - startY;
    // Solo swipe horizontal claro (> 42px y dominante frente al scroll vertical).
    if (Math.abs(dx) > 42 && Math.abs(dx) > Math.abs(dy) * 1.3) {
      setUserInteractedMobile(true);
      applyStageInPlace(dx < 0 ? activeStage + 1 : activeStage - 1);
    }
  };

  const active = STAGES[activeStage]!;

  return (
    <section
      id="showcase"
      data-theme="dark"
      className="bg-[var(--background)] text-foreground"
      aria-labelledby="showcase-heading"
    >
      {/* Runway alto + interior sticky en desktop; flujo natural h-auto en móvil/tablet sin secuestrar el scroll */}
      <div
        ref={containerRef}
        className="relative h-auto lg:h-[var(--runway-h)]"
        style={
          {
            "--runway-h": `calc(100dvh + ${STAGES.length * PANEL_SCROLL_DVH}dvh)`,
          } as CSSProperties
        }
      >
        <div className="relative flex h-auto flex-col overflow-hidden py-10 sm:py-12 lg:sticky lg:top-0 lg:h-screen lg:h-[100dvh] lg:py-0">
          <div className="mx-auto grid w-full max-w-container-wide flex-1 gutter-fluid min-h-0 grid-cols-1 items-center content-center gap-6 py-2 sm:gap-8 sm:py-4 lg:grid-cols-12 lg:gap-12 lg:py-0">
            {/* Izquierda: título + subtítulo + selector */}
            <div className="lg:col-span-5">
              <h2
                id="showcase-heading"
                className="text-balance text-[var(--step-3)] font-bold tracking-tight text-foreground"
              >
                {t("showcaseTitle")}
              </h2>
              <p className="mt-3 max-w-[46ch] text-sm leading-relaxed text-muted-foreground sm:text-[15px]">
                {t("showcaseSubtitle")}
              </p>

              {/* En pantallas < 1024px (móvil/tablet): selector compacto de píldoras + tarjeta de descripción fija sin saltos de altura al hacer scroll */}
              <div className="mt-5 lg:hidden">
                <div className="flex items-center justify-between gap-2 pb-2.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {STAGES.map((stage, idx) => {
                      const isActive = activeStage === idx;
                      const Icon = STAGE_ICONS[stage.id];
                      return (
                        <button
                          key={stage.id}
                          type="button"
                          onClick={() => jumpTo(idx)}
                          aria-current={isActive ? "location" : undefined}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${
                            isActive
                              ? "border-[var(--border-strong)] bg-[var(--foreground)] text-[var(--background)] shadow-xs"
                              : "border-[var(--border)] bg-[var(--card)]/70 text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          <Icon className="size-3" aria-hidden="true" />
                          <span className="font-mono text-[11px] opacity-75">
                            0{idx + 1}
                          </span>
                          <span className="max-w-[18ch] truncate sm:max-w-none">
                            {t(stage.titleKey)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      onClick={() => jumpTo(activeStage - 1)}
                      aria-label={t(
                        STAGES[
                          (activeStage - 1 + STAGES.length) % STAGES.length
                        ]!.titleKey,
                      )}
                      className="inline-flex size-8 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)] text-foreground transition-colors hover:border-[var(--border-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                    >
                      <ChevronLeft className="size-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => jumpTo(activeStage + 1)}
                      aria-label={t(
                        STAGES[(activeStage + 1) % STAGES.length]!.titleKey,
                      )}
                      className="inline-flex size-8 items-center justify-center rounded-full border border-[var(--border)] bg-[var(--card)] text-foreground transition-colors hover:border-[var(--border-strong)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                    >
                      <ChevronRight className="size-4" aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="rounded-xl border border-[var(--border)] bg-[var(--card)]/50 p-3.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {t(active.titleKey)}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      0{activeStage + 1} / 0{STAGES.length}
                    </span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                    {t(active.descKey)}
                  </p>
                </div>
              </div>

              {/* En PC (lg >= 1024px): lista táctil minimalista y sin recuadros en inactivos */}
              <nav
                className="mt-6 hidden lg:mt-7 lg:flex lg:flex-col lg:space-y-1.5"
                aria-labelledby="showcase-heading"
              >
                {STAGES.map((stage, idx) => {
                  const isActive = activeStage === idx;
                  const Icon = STAGE_ICONS[stage.id];
                  return (
                    <button
                      key={stage.id}
                      type="button"
                      onClick={() => jumpTo(idx)}
                      aria-current={isActive ? "location" : undefined}
                      className={`group relative flex items-start gap-3 rounded-xl px-3.5 py-3 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ${
                        isActive
                          ? "border border-border/80 bg-card/70 shadow-xs"
                          : "border border-transparent bg-transparent text-muted-foreground hover:bg-muted/20 hover:text-foreground"
                      }`}
                    >
                      {/* Left icon — unboxed on inactive, neat on active */}
                      <div
                        className={`mt-0.5 flex size-5 shrink-0 items-center justify-center transition-colors ${
                          isActive
                            ? "text-foreground"
                            : "text-muted-foreground/60 group-hover:text-foreground"
                        }`}
                      >
                        <Icon className="size-4" aria-hidden="true" />
                      </div>

                      {/* Content */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className={`text-sm tracking-tight transition-colors ${
                              isActive
                                ? "font-semibold text-foreground"
                                : "font-medium text-muted-foreground group-hover:text-foreground"
                            }`}
                          >
                            {t(stage.titleKey)}
                          </span>
                          <span
                            className={`font-mono text-[11px] transition-colors ${
                              isActive
                                ? "font-semibold text-foreground"
                                : "text-muted-foreground/40 group-hover:text-muted-foreground"
                            }`}
                          >
                            0{idx + 1}
                          </span>
                        </div>
                        {isActive && (
                          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                            {t(stage.descKey)}
                          </p>
                        )}
                      </div>

                      {/* Scroll fill line — only visible when active */}
                      <span
                        className={`absolute bottom-0 left-3.5 right-3.5 h-[1.5px] origin-left rtl:origin-right rounded-full showcase-fill showcase-fill-${idx} transition-opacity duration-300`}
                        style={{
                          background: "var(--foreground)",
                          opacity: isActive ? 0.9 : 0,
                        }}
                        aria-hidden="true"
                      />
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Derecha: animación interactiva en vivo (con soporte swipe horizontal en móvil/tablet) */}
            <div className="relative flex min-h-0 flex-col justify-center lg:col-span-7">
              <div
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                className="relative mx-auto w-full overflow-hidden rounded-[var(--radius-panel)] border border-[var(--border-strong)] bg-[var(--card)]/90 shadow-2xl dark:shadow-[0_25px_60px_-15px_rgba(0,0,0,0.7),inset_0_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur-md min-h-[460px] sm:min-h-[480px] lg:min-h-[500px] transition-shadow duration-300"
              >
                {/* Hairline superior glowing con el color de acento */}
                <div
                  className="pointer-events-none absolute inset-x-8 top-0 z-20 h-px transition-all duration-700"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${active.accent}, transparent)`,
                  }}
                  aria-hidden="true"
                />

                <div
                  key={active.id}
                  className={`h-full w-full ${
                    dir === 1 ? "showcase-enter-down" : "showcase-enter-up"
                  }`}
                >
                  <ShowcaseStageAnimation
                    key={active.id}
                    stageId={active.id}
                    t={t}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
